import {
  MenuItem,
  ToggleButton as MuiToggleButton,
  ToggleButtonGroup as MuiToggleButtonGroup,
  Stack,
  styled,
} from '@mui/material';
import {
  DatePicker,
  DateTimePicker,
  TimePicker,
  type DatePickerProps,
  type DateTimePickerProps,
  type TimePickerProps,
} from '@mui/x-date-pickers';
import type { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { DetailSelect } from '~/components/forms/details';
import { daysOfWeek, weekdayNames, type Day } from '~/components/schedule/lib/helpers';
import {
  SettingDescription,
  SettingInputWrapper,
  SettingLabelWrapper,
  SettingName,
  SettingsData,
  useSettingIds,
} from '~/components/settings';
import { DeviceSignalFrequency } from '~/generated/graphql';
import { uiLanguage } from '~/lib/i18n';
import { isScheduled, repeats, toRunAt, type SignalScheduleFormik } from './useSchedule';

const ToggleButtonGroup = styled(MuiToggleButtonGroup)(({ theme }) => ({
  '&.MuiToggleButtonGroup-root': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
}));

const ToggleButton = styled(MuiToggleButton)(({ theme }) => ({
  borderRadius: '20px !important',
  width: '40px',
  height: '40px',
  borderLeft: '1px solid #eee !important',
  padding: theme.spacing(1),
}));

// The pickers' text fields take the settings row's id, so the row's SettingName labels them.
const useRowTextField = (inputProps?: { readOnly: boolean }) => {
  const ids = useSettingIds();
  return { textField: { id: ids?.inputId, fullWidth: true, inputProps } };
};

const RowDateTimePicker = (props: Omit<DateTimePickerProps<DateTime>, 'slotProps'>) => {
  const slotProps = useRowTextField({ readOnly: true });
  return <DateTimePicker {...props} slotProps={slotProps} />;
};

const RowTimePicker = (props: Omit<TimePickerProps<DateTime>, 'slotProps'>) => {
  const slotProps = useRowTextField();
  return <TimePicker {...props} slotProps={slotProps} />;
};

const RowDatePicker = (props: Omit<DatePickerProps<DateTime>, 'slotProps'>) => {
  const slotProps = useRowTextField({ readOnly: true });
  return <DatePicker {...props} slotProps={slotProps} />;
};

export interface SignalScheduleFieldsProps {
  /** Event signals run at their event's time, so the schedule is shown off and locked. */
  disabled?: boolean;
  formik: SignalScheduleFormik;
}

export const ScheduleFields = ({ disabled = false, formik }: SignalScheduleFieldsProps) => {
  const { t } = useTranslation();
  const dayNames = weekdayNames(uiLanguage());
  const { frequency, weekDays, endType, startsOn, startTime } = formik.values;
  const scheduled = isScheduled(frequency);
  const recurring = repeats(frequency);
  const runAt = toRunAt(startsOn, startTime);

  if (disabled) {
    return (
      <SettingsData className="last">
        <SettingLabelWrapper>
          <SettingName>{t('pages.device_signals.schedule_section.schedule_label')}</SettingName>
          <SettingDescription>
            {t('pages.device_signals.schedule_section.event_description')}
          </SettingDescription>
        </SettingLabelWrapper>
        <SettingInputWrapper>
          <DetailSelect disabled value="NONE">
            <MenuItem value="NONE">
              {t('pages.device_signals.schedule_section.schedule_none')}
            </MenuItem>
          </DetailSelect>
        </SettingInputWrapper>
      </SettingsData>
    );
  }

  return (
    <>
      <SettingsData className={scheduled ? '' : 'last'}>
        <SettingLabelWrapper>
          <SettingName>{t('pages.device_signals.schedule_section.schedule_label')}</SettingName>
          <SettingDescription>
            {t('pages.device_signals.schedule_section.schedule_description')}
          </SettingDescription>
        </SettingLabelWrapper>
        <SettingInputWrapper>
          <Stack spacing={1}>
            <DetailSelect
              value={frequency}
              onChange={(event) => formik.setFieldValue('frequency', event.target.value)}
            >
              <MenuItem value="NONE">
                {t('pages.device_signals.schedule_section.schedule_none')}
              </MenuItem>
              <MenuItem value={DeviceSignalFrequency.Once}>
                {t('pages.device_signals.schedule_section.schedule_once')}
              </MenuItem>
              <MenuItem value={DeviceSignalFrequency.Daily}>
                {t('components.schedule.fields.repeat_daily')}
              </MenuItem>
              <MenuItem value={DeviceSignalFrequency.Weekly}>
                {t('components.schedule.fields.repeat_weekly')}
              </MenuItem>
            </DetailSelect>
            {frequency === DeviceSignalFrequency.Weekly && (
              <ToggleButtonGroup
                aria-label={t('components.schedule.fields.days_of_week')}
                onChange={(_, newValue: Day[]) => {
                  if (newValue.length) void formik.setFieldValue('weekDays', newValue);
                }}
                value={weekDays}
              >
                {daysOfWeek.map((day, index) => (
                  <ToggleButton key={day} value={day} aria-label={dayNames[index]}>
                    {t(`components.schedule.fields.days.${day.toLowerCase()}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          </Stack>
        </SettingInputWrapper>
      </SettingsData>

      {scheduled && !recurring && (
        <SettingsData className="last">
          <SettingLabelWrapper>
            <SettingName>{t('pages.device_signals.schedule_section.run_at_label')}</SettingName>
            <SettingDescription>
              {t('pages.device_signals.schedule_section.run_at_description')}
            </SettingDescription>
          </SettingLabelWrapper>
          <SettingInputWrapper>
            <RowDateTimePicker
              format="EEEE, DDD, t"
              timeSteps={{ minutes: 15 }}
              skipDisabled
              thresholdToRenderTimeInASingleColumn={200}
              value={runAt}
              onChange={(value) => {
                void formik.setFieldValue('startsOn', value?.startOf('day') ?? null);
                void formik.setFieldValue('startTime', value);
              }}
            />
          </SettingInputWrapper>
        </SettingsData>
      )}

      {recurring && (
        <>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('pages.device_signals.schedule_section.at_label')}</SettingName>
              <SettingDescription>
                {t('pages.device_signals.schedule_section.at_description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <RowTimePicker
                timeSteps={{ minutes: 15 }}
                skipDisabled
                thresholdToRenderTimeInASingleColumn={200}
                value={formik.values.startTime}
                onChange={(value) => formik.setFieldValue('startTime', value)}
              />
            </SettingInputWrapper>
          </SettingsData>

          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('pages.device_signals.schedule_section.starts_label')}</SettingName>
              <SettingDescription>
                {t('pages.device_signals.schedule_section.starts_description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <RowDatePicker
                format="EEEE, DDD"
                value={formik.values.startsOn}
                onChange={(value) => formik.setFieldValue('startsOn', value)}
              />
            </SettingInputWrapper>
          </SettingsData>
        </>
      )}

      {recurring && (
        <SettingsData className="last">
          <SettingLabelWrapper>
            <SettingName>{t('pages.device_signals.schedule_section.ends_label')}</SettingName>
            <SettingDescription>
              {t('pages.device_signals.schedule_section.ends_description')}
            </SettingDescription>
          </SettingLabelWrapper>
          <SettingInputWrapper>
            <Stack spacing={1}>
              <DetailSelect
                value={endType}
                onChange={(event) => formik.setFieldValue('endType', event.target.value)}
              >
                <MenuItem value="Never">{t('components.schedule.fields.end_never')}</MenuItem>
                <MenuItem value="On date">{t('components.schedule.fields.end_on')}</MenuItem>
              </DetailSelect>
              {endType === 'On date' && (
                <DatePicker
                  format="EEEE, DDD"
                  value={formik.values.endsOn}
                  onChange={(value) => formik.setFieldValue('endsOn', value)}
                  slotProps={{
                    textField: {
                      error: Boolean(formik.errors.endsOn),
                      helperText: formik.errors.endsOn,
                      inputProps: { readOnly: true },
                    },
                  }}
                />
              )}
            </Stack>
          </SettingInputWrapper>
        </SettingsData>
      )}
    </>
  );
};
